import { fetchToken } from '../helpers/fetch';
import { const_routes } from '../helpers/const_routes';
import { withSearchIntentContext } from '../helpers/userInterestPayloadHelper';
import { types } from '../types/types';
import { startLogout } from './authActions';
import Swal from 'sweetalert2';
import { t } from 'i18next';

const handleAuthErrors = (dispatch, body) => {
  if (!body || body.ok !== false) return false;
  if (body.code === 'bak-aut-tok-inv-001') {
    Swal.fire(t('titles.error'), t('alerts.validation.bak-aut-tok-inv-001'), 'error');
    dispatch(startLogout());
    return true;
  }
  if (body.code === 'bak-aut-tok-inv-002' || body.code === 'bak-aut-tok-001' || body.code === 'bak-aut-tok-002') {
    Swal.fire(t('titles.error'), t('api.token.timed_out'), 'error');
    dispatch(startLogout());
    return true;
  }
  if (body.code === 'bak-error-001') {
    Swal.fire(
      t('titles.error'),
      `${t('alerts.validation.bak-error-001')} - ${body.method || ''}`,
      'error'
    );
    return true;
  }
  return false;
};

const buildInterestQuery = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, value);
    }
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

/**
 * Registra intención (bridge_click | record_view). Fire-and-forget amigable.
 * Adjunta searchinfo_id de la búsqueda activa y originId = originIds[0].
 */
export const startRegisterUserInterest = (payload) => {
  return async (_dispatch, getState) => {
    try {
      const body = withSearchIntentContext(payload, getState());
      const resp = await fetchToken(
        `${const_routes.SCHEMA}`,
        `${const_routes.VERSIONV1}`,
        `${const_routes.ROUTE_USER_INTEREST}`,
        'add',
        body,
        'POST'
      );
      return await resp.json();
    } catch (error) {
      console.error('startRegisterUserInterest', error);
      return { ok: false, msg: String(error) };
    }
  };
};

/**
 * Solicitud de soporte adicional (respeta flags en server).
 */
export const startRegisterSupportRequest = (payload) => {
  return async (_dispatch, getState) => {
    try {
      const body = withSearchIntentContext(payload, getState());
      const resp = await fetchToken(
        `${const_routes.SCHEMA}`,
        `${const_routes.VERSIONV1}`,
        `${const_routes.ROUTE_USER_INTEREST}`,
        'support',
        body,
        'POST'
      );
      return await resp.json();
    } catch (error) {
      console.error('startRegisterSupportRequest', error);
      return { ok: false, msg: String(error) };
    }
  };
};

export const startDownloadSupportInboxExcel = (filters = {}) => {
  return async (dispatch) => {
    try {
      const resp = await fetchToken(
        `${const_routes.SCHEMA}`,
        `${const_routes.VERSIONV1}`,
        `${const_routes.ROUTE_USER_INTEREST}`,
        `export-excel${buildInterestQuery({ ...filters, scope: 'inbox' })}`
      );
      const contentType = (resp.headers.get('content-type') || '').toLowerCase();
      if (!resp.ok || contentType.includes('application/json')) {
        const body = await resp.json().catch(() => ({}));
        if (handleAuthErrors(dispatch, body)) {
          return body;
        }
        Swal.fire(t('titles.error'), body.msg || t('alerts.validation.bak-error-001'), 'error');
        return { ok: false, msg: body.msg || 'download-error' };
      }
      const blob = await resp.blob();
      const disposition = resp.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = (match && match[1]) || 'mis_soportes.xlsx';
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      return { ok: true, filename };
    } catch (error) {
      Swal.fire(t('titles.error'), String(error), 'error');
      return { ok: false, msg: String(error) };
    }
  };
};

export const startLoadingInterestsPaginated = (page, size, filters = {}) => {
  return async (dispatch) => {
    dispatch({ type: types.USER_INTEREST_SET_LOADING, payload: true });
    try {
      const resp = await fetchToken(
        `${const_routes.SCHEMA}`,
        `${const_routes.VERSIONV1}`,
        `${const_routes.ROUTE_USER_INTEREST}`,
        `get/${page}/${size}${buildInterestQuery(filters)}`
      );
      const body = await resp.json();
      if (handleAuthErrors(dispatch, body)) {
        dispatch({ type: types.USER_INTEREST_SET_LOADING, payload: false });
        return body;
      }
      if (body.ok) {
        dispatch({
          type: types.USER_INTEREST_START_LOADING_PAGINATED,
          payload: body.interestsPaginated,
        });
      } else {
        Swal.fire(t('titles.error'), body.msg || t('alerts.validation.bak-error-001'), 'error');
      }
      dispatch({ type: types.USER_INTEREST_SET_LOADING, payload: false });
      return body;
    } catch (error) {
      dispatch({ type: types.USER_INTEREST_SET_LOADING, payload: false });
      Swal.fire(t('titles.error'), String(error), 'error');
      return { ok: false, msg: String(error) };
    }
  };
};

export const startLoadingInterestDetail = (id) => {
  return async (dispatch) => {
    dispatch({ type: types.USER_INTEREST_SET_LOADING, payload: true });
    try {
      const resp = await fetchToken(
        `${const_routes.SCHEMA}`,
        `${const_routes.VERSIONV1}`,
        `${const_routes.ROUTE_USER_INTEREST}`,
        `get/detail/${id}`
      );
      const body = await resp.json();
      if (handleAuthErrors(dispatch, body)) {
        dispatch({ type: types.USER_INTEREST_SET_LOADING, payload: false });
        return body;
      }
      if (body.ok) {
        dispatch({
          type: types.USER_INTEREST_SELECT,
          payload: {
            interest: body.interest,
            origins: body.origins || [],
            target: body.target || null,
          },
        });
      } else {
        Swal.fire(t('titles.error'), body.msg || t('alerts.validation.bak-error-001'), 'error');
      }
      dispatch({ type: types.USER_INTEREST_SET_LOADING, payload: false });
      return body;
    } catch (error) {
      dispatch({ type: types.USER_INTEREST_SET_LOADING, payload: false });
      Swal.fire(t('titles.error'), String(error), 'error');
      return { ok: false, msg: String(error) };
    }
  };
};

export const startUpdateSupportStatus = (id, status, notes) => {
  return async (dispatch) => {
    try {
      const bodyPayload = { status };
      if (notes !== undefined) bodyPayload.notes = notes;
      const resp = await fetchToken(
        `${const_routes.SCHEMA}`,
        `${const_routes.VERSIONV1}`,
        `${const_routes.ROUTE_USER_INTEREST}`,
        `support/status/${id}`,
        bodyPayload,
        'PUT'
      );
      const body = await resp.json();
      if (handleAuthErrors(dispatch, body)) {
        return body;
      }
      if (!body.ok) {
        Swal.fire(t('titles.error'), body.msg || t('alerts.validation.bak-error-001'), 'error');
      }
      return body;
    } catch (error) {
      Swal.fire(t('titles.error'), String(error), 'error');
      return { ok: false, msg: String(error) };
    }
  };
};

export const cleanUserInterestSelection = () => ({
  type: types.USER_INTEREST_CLEAN,
});
